import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError, getCollection } from "../api";
import BackLink from "../components/BackLink";
import { BookList } from "../components/BookList";
import { useT } from "../i18n";

/**
 * CollectionPage is one curated collection: its books, in the order the admin
 * chose (requirement 0119). Only the books this viewer may read arrive, so a
 * collection with none for them is a 404, said plainly.
 */
export default function CollectionPage() {
  const t = useT();
  const { collectionID = "" } = useParams();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ["collections", collectionID],
    queryFn: () => getCollection(collectionID),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const missing = error instanceof ApiError && error.status === 404;

  return (
    <main className="mx-auto max-w-5xl px-4 pt-5 pb-12">
      <BackLink to="/browse">{t.collections.back}</BackLink>
      {data && (
        <header className="mt-3 mb-4">
          <h1
            dir="auto"
            data-testid="collection-name"
            className="text-lg font-semibold tracking-tight"
          >
            {data.name}
          </h1>
          {data.description && (
            <p dir="auto" className="mt-0.5 text-sm text-muted">
              {data.description}
            </p>
          )}
        </header>
      )}
      {missing ? (
        <p data-testid="collection-missing" className="mt-6 text-sm text-muted">
          {t.collections.notFound}
        </p>
      ) : (
        <BookList
          books={data?.books ?? []}
          isPending={isPending}
          error={isError ? new Error(t.collections.loadFailed) : null}
          onRetry={() => void refetch()}
          empty={null}
          savable
          testID="collection-books"
        />
      )}
    </main>
  );
}
